<template>
  <div class="ean-page">
    <!-- Header -->
    <div class="ean-header">
      <div>
        <span class="header-eyebrow">Shopify</span>
        <h2 class="header-title">Gestion EAN / Barcodes</h2>
      </div>
      <div class="header-actions">
        <button class="btn-ean btn-ean--ghost" @click="refreshProducts()" :disabled="refreshing">
          <i :class="refreshing ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-rotate'"></i>
          {{ refreshing ? 'Actualizando...' : 'Actualizar productos' }}
        </button>
      </div>
    </div>

    <!-- KPI Strip -->
    <div class="kpi-strip">
      <div class="kpi-card">
        <span class="kpi-label">EAN libres</span>
        <span class="kpi-value kpi-value--green">{{ eanLibres }}</span>
        <span class="kpi-foot">disponibles</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Sin EAN</span>
        <span class="kpi-value kpi-value--warning">{{ productosSinEan.length }}</span>
        <span class="kpi-foot">productos</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Prefijos</span>
        <span class="kpi-value">{{ dataPrefixAll.length }}</span>
        <span class="kpi-foot">bloques</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Total EAN</span>
        <span class="kpi-value">{{ dataMatchAll.length }}</span>
        <span class="kpi-foot">registrados</span>
      </div>
    </div>

    <!-- Stock diferente (solo local) -->
    <div class="section-card" v-if="modoLocal && productosDiferenteStock.length > 0">
      <div class="section-card__header">
        <h3 class="section-card__title">
          <i class="fa-solid fa-triangle-exclamation" style="color: #F59E0B"></i>
          SKU con diferente stock
        </h3>
        <span class="section-card__badge">{{ productosDiferenteStock.length }}</span>
      </div>
      <div class="table-wrapper">
        <b-table
          :items="productosDiferenteStock"
          :fields="columnasStock"
          class="mb-0"
          hover
          small
          responsive
          thead-class="table-head"
          tbody-tr-class="table-row"
        >
          <template #cell(_img)="data">
            <img v-if="data.item.img" :src="data.item.img" class="cell-img" />
          </template>
          <template #cell(_id)="data">
            <a :href="data.item.productHref" target="_blank" class="cell-link">{{ data.item.id }}</a>
          </template>
          <template #cell(_idVariant)="data">
            <a :href="data.item.variantHref" target="_blank" class="cell-link">{{ data.item.idVariant }}</a>
          </template>
        </b-table>
      </div>
    </div>

    <!-- Instrucciones (colapsable) -->
    <div class="section-card section-card--compact">
      <button class="section-card__toggle" @click="showInstrucciones = !showInstrucciones">
        <i :class="showInstrucciones ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
        Instrucciones de uso
      </button>
      <transition name="slide">
        <div v-if="showInstrucciones" class="instrucciones">
          <ol class="instrucciones__list">
            <li>Descargar de GS1 el fichero xlsx</li>
            <li>Subirlo al ERP con el formulario de abajo</li>
            <li>Seleccionar todos los productos sin EAN y asociarlos</li>
            <li>Abrir el fichero descargado de GS1 en Excel Online</li>
            <li>Pulsar el boton de copiar en el ERP</li>
            <li>Fijarse en la columna "Situate en"</li>
            <li>En Excel Online situarte en esa fila (columna 2) y pegar</li>
            <li>Descargar el Excel y subirlo a GS1</li>
          </ol>
        </div>
      </transition>
    </div>

    <!-- Subir archivo -->
    <div class="section-card">
      <div class="section-card__header">
        <h3 class="section-card__title"><i class="fa-solid fa-upload"></i> Subir archivo GS1</h3>
      </div>
      <div class="upload-row">
        <b-form-file
          v-model="csvInput"
          placeholder="Selecciona un archivo .xlsx..."
          drop-placeholder="Suelta aqui..."
          class="upload-input"
        />
        <button class="btn-ean btn-ean--primary" @click="procesarXls">
          <i class="fa-solid fa-gear"></i> Procesar
        </button>
      </div>
    </div>

    <!-- Listado Excel / Prefijos -->
    <div class="section-card">
      <div class="section-card__header">
        <h3 class="section-card__title">Prefijos EAN</h3>
      </div>
      <div class="table-wrapper">
        <b-table
          :items="dataPrefixAll"
          :fields="columnasExcel"
          class="mb-0"
          hover
          small
          responsive
          show-empty
          empty-text="Sin prefijos cargados"
          thead-class="table-head"
          tbody-tr-class="table-row"
        >
          <template #cell(_opt)="data">
            <button class="action-btn action-btn--copy" @click="copiarAlPortapapeles(data.item)" title="Copiar para Excel">
              <i :style="data.item.hasSkuWithoutDate ? 'color: #EF4444' : ''" class="fa-solid fa-copy"></i>
            </button>
          </template>
          <template #cell(prefix)="data">
            <span class="cell-mono">{{ data.item.prefix }}</span>
          </template>
          <template #cell(count)="data">
            <span class="cell-mono">{{ data.item.count }}</span>
          </template>
          <template #cell(emptySkuCount)="data">
            <span class="cell-count-badge" v-if="data.item.emptySkuCount > 0">{{ data.item.emptySkuCount }}</span>
            <span class="cell-muted" v-else>0</span>
          </template>
          <template #cell(ultimoSinActualizar)="data">
            <span class="cell-mono cell-mono--accent" v-if="data.item.ultimoSinActualizar">{{ data.item.ultimoSinActualizar }}</span>
            <span class="cell-muted" v-else>--</span>
          </template>
        </b-table>
      </div>
    </div>

    <!-- Productos sin EAN -->
    <div class="section-card">
      <div class="section-card__header">
        <h3 class="section-card__title">Productos sin EAN</h3>
        <span class="section-card__badge section-card__badge--warning">{{ productosSinEan.length }}</span>
      </div>

      <div class="action-bar">
        <div class="action-bar__left">
          <button class="btn-mini" @click="seleccionarTodos(!algunoCheck)">
            <i :class="algunoCheck ? 'fa-solid fa-xmark' : 'fa-solid fa-check-double'"></i>
            {{ algunoCheck ? 'Deseleccionar' : 'Seleccionar todos' }}
          </button>
        </div>
        <div class="action-bar__right" v-if="algunoCheck">
          <span class="action-bar__count">{{ cantidadCheck }} seleccionados</span>
          <button v-if="cantidadCheck <= eanLibres" class="btn-ean btn-ean--green btn-ean--sm" @click="asociarClick()">
            <i class="fa-solid fa-link"></i> Asociar EAN
          </button>
          <span v-else class="faltan-badge">
            <i class="fa-solid fa-triangle-exclamation"></i> Faltan {{ cantidadCheck - eanLibres }} codigos
          </span>
        </div>
      </div>

      <div class="table-wrapper">
        <b-table
          :items="productosSinEan"
          :fields="columnasProductos"
          class="mb-0"
          hover
          small
          responsive
          show-empty
          empty-text="Todos los productos tienen EAN"
          thead-class="table-head"
          tbody-tr-class="table-row"
        >
          <template #cell(_img)="data">
            <img v-if="data.item.img" :src="data.item.img" class="cell-img" />
            <div v-else class="cell-img-empty"><i class="fa-solid fa-image"></i></div>
          </template>
          <template #cell(_check)="data">
            <span @click="itemCheck()">
              <b-form-checkbox v-model="data.item.check"></b-form-checkbox>
            </span>
          </template>
          <template #cell(sku)="data">
            <span class="cell-sku">{{ data.item.sku }}</span>
          </template>
          <template #cell(id)="data">
            <span class="cell-mono">{{ data.item.id }}</span>
          </template>
        </b-table>
      </div>
      <div v-infinite-scroll="loadMoreMatch" infinite-scroll-disabled="busy" infinite-scroll-distance="10"></div>
    </div>

    <!-- Listado EAN completo -->
    <div class="section-card">
      <div class="section-card__header">
        <h3 class="section-card__title">Listado EAN</h3>
        <div class="search-box">
          <i class="fa-solid fa-magnifying-glass search-box__icon"></i>
          <input type="text" class="search-box__input" placeholder="Buscar EAN, SKU..." v-model="buscarMatch" />
        </div>
      </div>
      <div class="table-wrapper">
        <b-table
          :items="dataMatchBusqueda"
          :fields="columnasListado"
          class="mb-0"
          hover
          small
          responsive
          show-empty
          empty-text="Sin resultados"
          thead-class="table-head"
          tbody-tr-class="table-row"
        >
          <template #cell(prefix)="data">
            <span class="cell-mono">{{ data.item.prefix }}</span>
          </template>
          <template #cell(number)="data">
            <span class="cell-mono">{{ data.item.number }}</span>
          </template>
          <template #cell(sku)="data">
            <span class="cell-sku" v-if="data.item.sku">{{ data.item.sku }}</span>
            <span class="cell-muted" v-else>--</span>
          </template>
          <template #cell(updated)="data">
            <span class="cell-mono" v-if="data.item.updated">{{ data.item.updated }}</span>
            <span class="cell-muted" v-else>--</span>
          </template>
        </b-table>
      </div>
      <div v-infinite-scroll="loadMoreMatch" infinite-scroll-disabled="busy" infinite-scroll-distance="10"></div>
    </div>
  </div>
</template>

<script>
import axios from '@axios'
import vSelect from 'vue-select'
import BCardCode from '@core/components/b-card-code'
import { BRow, BCol, BFormFile, BButton  } from 'bootstrap-vue'
import { BTable } from 'bootstrap-vue'
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import * as XLSX from 'xlsx';

export default {
  components: {
    BCardCode, BRow, BCol, BFormFile, BButton, vSelect, BTable, ToastificationContent,
  },
  data(){
    return{
      showInstrucciones: false,
      columnasExcel: [
        { key: "_opt", label: "", sortable: false, thStyle: { width: "40px" } },
        { key: "prefix", label: "Prefijo", sortable: true },
        { key: "count", label: "Total", sortable: true },
        { key: "emptySkuCount", label: "Disponibles", sortable: true },
        { key: "ultimoSinActualizar", label: "Situate en", sortable: true },
      ],
      columnasListado: [
        { key: "prefix", label: "Prefijo", sortable: true, thStyle: { width: "80px" } },
        { key: "number", label: "Codigo", sortable: true },
        { key: "sku", label: "SKU", sortable: true },
        { key: "updated", label: "Fecha", sortable: true },
      ],
      columnasProductos: [
        { key: "_check", label: "", thStyle: { width: "40px" } },
        { key: "_img", label: "", thStyle: { width: "60px" } },
        { key: "product", label: "Producto", sortable: true },
        { key: "sku", label: "SKU", sortable: true },
        { key: "id", label: "ID Shopify", sortable: true, thStyle: { width: "120px" } },
      ],
      columnasStock: [
        { key: "_img", label: "", thStyle: { width: "60px" } },
        { key: "sku", label: "SKU", sortable: true },
        { key: "inventory_quantity", label: "Stock", sortable: true, thStyle: { width: "80px" } },
        { key: "_id", label: "ID Product", sortable: true },
        { key: "_idVariant", label: "ID Variant", sortable: true },
      ],
      csvInput: [],
      dataMatchAll: [],
      dataMatchBusqueda: [],
      dataPrefixAll: [],
      buscarMatch: '',
      countItemView: 50,
      nextScrollItemView: 40,
      defaultItemView: 50,
      productosAll: [],
      productosSinEan: [],
      productosDiferenteStock: [],
      eanLibres: 0,
      algunoCheck: false,
      cantidadCheck: 0,
      newSku: '',
      modoLocal: false,
      refreshing: false
    }
  },
  methods:{
    refreshProducts(){
      this.refreshing = true;
      this.productosSinEan = [];

      axios.get("/shopify/products?refresh=true")
        .then(response => {
          this.productosAll = response.data;
          const excludedSkus = ["COURIER-FEE","DEBOSS-CUSTOMIZATION","E-GIFT-CARD-300","GIFT-PACKAGING","LEATHER-CUSTOMIZATION"];

          this.productosAll.forEach(product => {
            const variantsWithoutBarcode = product.variants.filter(variant => !variant.barcode && !excludedSkus.includes(variant.sku) && variant.sku != null && variant.sku.length > 1);
            variantsWithoutBarcode.forEach(variant => {
              const image = product.images.find(image => image.id === variant.image_id);
              const src = image ? image.src : '';
              this.productosSinEan.push({ check: false, product: product.title, sku: variant.sku, img: src, id: product.id, productId: product.admin_graphql_api_id, variantId: variant.admin_graphql_api_id });
            });
          });

          this.refreshing = false;
          this.$toast({ component: ToastificationContent, props: { title: "Productos actualizados", icon: "RefreshCwIcon", variant: "success" } });
        })
        .catch(error => {
          console.error("Error refreshing products:", error);
          this.refreshing = false;
          this.$toast({ component: ToastificationContent, props: { title: "Error al actualizar productos", icon: "AlertTriangleIcon", variant: "danger" } });
        });
    },
    loadItems(){
      axios.post("/uso", {seccion:"ean-list"});
      this.eanLibres = 0;
      this.productosSinEan = [];
      this.productosDiferenteStock = [];

      axios.get("/finanzas/ean").then(r => r.data).then(res => {
        this.dataMatchAll = res;
        this.dataMatchBusqueda = this.dataMatchAll.slice(0,this.defaultItemView);

        const prefixCount = res.reduce((acc, item) => {
          acc[item.prefix] = acc[item.prefix] || { count: 0, emptySkuCount: 0 };
          acc[item.prefix].count += 1;
          if (item.sku === "") acc[item.prefix].emptySkuCount += 1;
          else if (item.updated == '') acc[item.prefix].hasSkuWithoutDate = true;
          return acc;
        }, {});

        this.dataPrefixAll = Object.keys(prefixCount).map(prefix => ({
          prefix, count: prefixCount[prefix].count, emptySkuCount: prefixCount[prefix].emptySkuCount, hasSkuWithoutDate: prefixCount[prefix].hasSkuWithoutDate || false,
        }));

        this.dataPrefixAll.forEach(element => {
          this.eanLibres += element.emptySkuCount;
          let aux = this.dataMatchAll.find(item => item.prefix === element.prefix && item.updated === '' && item.sku != '');
          if(aux) element.ultimoSinActualizar = aux.number;
        });
      });

      Promise.all([axios.get("/shopify/products")]).then(([productsResponse]) => {
        this.productosAll = productsResponse.data;
        const excludedSkus = ["COURIER-FEE","DEBOSS-CUSTOMIZATION","E-GIFT-CARD-300","GIFT-PACKAGING","LEATHER-CUSTOMIZATION"];

        this.productosAll.forEach(product => {
          const variantsWithoutBarcode = product.variants.filter(variant => !variant.barcode && !excludedSkus.includes(variant.sku) && variant.sku != null && variant.sku.length > 1);
          variantsWithoutBarcode.forEach(variant => {
            const image = product.images.find(image => image.id === variant.image_id);
            const src = image ? image.src : '';
            this.productosSinEan.push({ check: false, product: product.title, sku: variant.sku, img: src, id: product.id, productId: product.admin_graphql_api_id, variantId: variant.admin_graphql_api_id });
          });
        });
      }).catch(error => { console.error("Error fetching data:", error); });
    },
    procesarXls(){
      var reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet);
        const firstValue = Object.values(jsonData[0])[0];
        const numberPrefix = firstValue.match(/\d+/)[0];

        let newDatos = jsonData
          .filter(row => Object.values(row)[19] !== undefined && Object.values(row)[19] !== '')
          .map(row => ({ number: Object.values(row)[0], name: Object.values(row)[17] != 'No' ? Object.values(row)[17] : '', sku: Object.values(row)[2]|| '', updated: Object.values(row)[19] || '', prefix: numberPrefix }));
        newDatos = newDatos.slice(2);

        axios.post("/finanzas/ean", { lineas: newDatos }).then(r => r.data).then(res => {
          this.loadItems();
          this.csvInput = [];
          this.$toast({ component: ToastificationContent, props: { title: "EAN actualizados correctamente", icon: "EditIcon", variant: "success" } });
        }).catch(e => {
          console.log(e);
          this.$toast({ component: ToastificationContent, props: { title: "Error en el formato", icon: "EditIcon", variant: "danger" } });
        })
      };
      reader.readAsArrayBuffer(this.csvInput);
    },
    comprobarSelectAll(){
      this.cantidadCheck = 0;
      this.productosSinEan.forEach(element => { if(element.check) this.cantidadCheck++; });
      this.algunoCheck = this.cantidadCheck > 0;
    },
    itemCheck(){
      setTimeout(() => { this.comprobarSelectAll(); }, 100);
    },
    seleccionarTodos(estado = true){
      this.productosSinEan.forEach(element => { element.check = estado; });
      this.comprobarSelectAll();
    },
    asociarClick(){
      let updateEan = [];
      this.productosSinEan.filter(product => product.check).forEach(product => {
        const ean = this.dataMatchAll.find(ean => ean.sku === '');
        if (ean) {
          ean.sku = product.sku; ean.name = product.sku;
          ean.productId = product.productId; ean.variantId = product.variantId;
          updateEan.push(ean);
        }
        product.check = false;
      });

      axios.post("/shopify/products/barcode", { products: updateEan }).then(r => r.data).then(res => {
        axios.post("/finanzas/ean", { lineas: updateEan }).then(r => r.data).then(eanRes => {
          this.comprobarSelectAll();
          this.buscarMatchFuncion();
          this.loadItems();
        })
      })
    },
    loadMoreMatch(){
      this.countItemView += this.nextScrollItemView;
      this.buscarMatchFuncion();
    },
    buscarMatchFuncion(items = this.dataMatchAll){
      let auxSorting = JSON.parse(JSON.stringify(items));
      let aux = auxSorting.filter(item => {
        return item.prefix.toLowerCase().includes(this.buscarMatch.toLowerCase()) || item.sku.toLowerCase().includes(this.buscarMatch.toLowerCase()) || item.number.toLowerCase().includes(this.buscarMatch.toLowerCase());
      });
      this.dataMatchBusqueda = aux.slice(0,this.countItemView);
    },
    copiarAlPortapapeles(data){
      let texto = '';
      this.dataMatchAll.forEach(item => {
        if (item.prefix == data.prefix && item.sku != '' && item.updated == '') {
          texto += `[ACTIVE]\t${item.sku}\tN/A\tN/A\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\tN/A\t\t[WEBSITE]\tN/A\t\t\t\t\t\t\tN/A\t[MANUFACTURER_PART_NUMBER] Manufacturer Part Number\t${item.sku}\t[FOR_INTERNAL_USE_1] Stock Keeping Unit\n`;
        }
      });
      navigator.clipboard.writeText(texto).then(() => {
        this.$toast({ component: ToastificationContent, props: { title: "EAN listos para pegar en el Excel", icon: "EditIcon", variant: "success" } });
      }).catch(err => {
        this.$toast({ component: ToastificationContent, props: { title: "Error al copiar", icon: "EditIcon", variant: "danger" } });
      });
    },
  },
  mounted() { this.loadItems(); },
  watch: {
    buscarMatch(){
      if(this.buscarMatch == "") this.countItemView = this.defaultItemView;
      this.buscarMatchFuncion();
    },
  }
}
</script>

<style lang="scss" scoped>
$primary: #3B82F6;
$primary-dark: #2563EB;
$success: #10B981;
$danger: #EF4444;
$warning: #F59E0B;
$border: #E2E8F0;
$text: #1E293B;
$text-muted: #64748B;
$bg: #F8FAFC;
$card-bg: #FFFFFF;
$radius: 12px;
$radius-sm: 8px;
$shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);

.ean-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 8px;
}

.ean-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 20px;
}

.header-eyebrow {
  display: block;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: $primary;
  margin-bottom: 2px;
}

.header-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: $text;
  margin: 0;
}

.header-actions { display: flex; gap: 8px; }

// Buttons
.btn-ean {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 20px;
  border-radius: $radius-sm;
  border: none;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &--primary { background: $primary; color: white; &:hover { background: $primary-dark; } }
  &--green { background: $success; color: white; &:hover { background: darken($success, 8%); } }
  &--ghost { background: transparent; color: $text-muted; border: 1px solid $border; &:hover { background: $bg; color: $text; } }
  &--sm { padding: 6px 14px; font-size: 12px; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

.btn-mini {
  padding: 5px 12px;
  border-radius: 6px;
  border: 1px solid $border;
  background: transparent;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  color: $text-muted;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: all 0.15s ease;
  &:hover { border-color: $primary; color: $primary; }
}

// KPI Strip
.kpi-strip {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.kpi-card {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius-sm;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  box-shadow: $shadow;
}

.kpi-label { font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; color: $text-muted; }
.kpi-value { font-family: 'JetBrains Mono', monospace; font-size: 22px; font-weight: 700; color: $text; font-feature-settings: "tnum"; margin: 4px 0 2px; &--green { color: $success; } &--warning { color: $warning; } }
.kpi-foot { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: $text-muted; }

// Section card
.section-card {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius;
  box-shadow: $shadow;
  overflow: hidden;
  margin-bottom: 16px;

  &--compact { overflow: visible; }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 22px;
    border-bottom: 1px solid $border;
  }

  &__title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 15px;
    font-weight: 700;
    color: $text;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 26px;
    height: 22px;
    padding: 0 8px;
    border-radius: 12px;
    background: rgba($primary, 0.08);
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    font-weight: 700;
    color: $primary;

    &--warning { background: rgba($warning, 0.1); color: darken($warning, 10%); }
  }

  &__toggle {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 22px;
    border: none;
    background: transparent;
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    font-weight: 600;
    color: $text-muted;
    cursor: pointer;
    text-align: left;
    transition: color 0.15s;
    &:hover { color: $primary; }
    i { font-size: 10px; }
  }
}

// Instrucciones
.instrucciones {
  padding: 0 22px 18px;

  &__list {
    margin: 0;
    padding-left: 20px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    color: $text-muted;
    line-height: 1.8;

    li::marker { color: $primary; font-weight: 700; }
  }
}

// Upload row
.upload-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 22px;
}

// Action bar
.action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 22px;
  background: rgba($primary, 0.03);
  border-bottom: 1px solid rgba($primary, 0.08);

  &__left { display: flex; gap: 8px; }
  &__right { display: flex; align-items: center; gap: 10px; }
  &__count { font-family: 'JetBrains Mono', monospace; font-size: 11px; font-weight: 600; color: $primary; }
}

.faltan-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 20px;
  background: rgba($danger, 0.1);
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 600;
  color: $danger;
}

// Search
.search-box {
  position: relative;
  width: 220px;
  &__icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: $text-muted; font-size: 12px; }
  &__input {
    width: 100%;
    padding: 7px 12px 7px 32px;
    border: 1px solid $border;
    border-radius: $radius-sm;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    color: $text;
    background: transparent;
    &::placeholder { color: #CBD5E1; }
    &:focus { outline: none; border-color: $primary; }
  }
}

// Table
.table-wrapper { padding: 0; }

// Los ::v-deep van bajo .ean-page a proposito: en la raiz, postcss-rtl + scoped generan [dir][data-v-xxx]
// y ninguna propiedad direccional (padding, margin...) llega a aplicarse.
.ean-page ::v-deep .table-head th {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: $text-muted;
  border-top: none;
  border-bottom: 1px solid $border;
  padding: 8px 14px;
  background: transparent;
}

.ean-page ::v-deep .table-row td {
  padding: 8px 14px;
  vertical-align: middle;
  border-color: lighten($border, 3%);
  font-size: 13px;
}

.ean-page ::v-deep .table-row:hover td { background: $bg; }

.cell-img { width: 70px; max-height: 50px; object-fit: contain; border-radius: 5px; }
.cell-img-empty { width: 70px; height: 50px; border-radius: 5px; border: 1px dashed $border; display: flex; align-items: center; justify-content: center; color: #CBD5E1; font-size: 13px; }
.cell-sku { font-family: 'JetBrains Mono', monospace; font-size: 12px; font-weight: 500; color: $text; }
.cell-mono { font-family: 'JetBrains Mono', monospace; font-size: 12px; color: $text; font-feature-settings: "tnum"; &--accent { color: $primary; font-weight: 600; } }
.cell-muted { color: #CBD5E1; font-size: 12px; }
.cell-link { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: $primary; text-decoration: none; &:hover { text-decoration: underline; } }

.cell-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: rgba($success, 0.1);
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 700;
  color: $success;
}

.action-btn {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: $text-muted;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  font-size: 12px;
  &--copy:hover { background: rgba($primary, 0.1); color: $primary; border-color: rgba($primary, 0.2); }
}

// Slide transition
.slide-enter-active, .slide-leave-active { transition: all 0.3s ease; max-height: 400px; overflow: hidden; }
.slide-enter, .slide-leave-to { max-height: 0; opacity: 0; }

// Responsive
@media (max-width: 768px) {
  .kpi-strip { grid-template-columns: repeat(2, 1fr); }
  .ean-header { flex-direction: column; align-items: flex-start; gap: 12px; }
  .action-bar { flex-direction: column; gap: 8px; align-items: flex-start; }
  .upload-row { flex-direction: column; }
  .search-box { width: 100%; }
}
</style>

<style lang="scss">
// Dark mode
.dark-layout {
  .ean-page {
    .header-title { color: #E2E8F0; }
    .kpi-card { background: #1E293B; border-color: #334155; }
    .kpi-value { color: #F1F5F9; }

    .section-card { background: #1E293B; border-color: #334155; }
    .section-card__header { border-color: #334155; }
    .section-card__title { color: #E2E8F0; }
    .section-card__toggle { color: #94A3B8; &:hover { color: #3B82F6; } }

    .instrucciones__list { color: #94A3B8; }

    .action-bar { background: rgba(#3B82F6, 0.05); border-color: rgba(#3B82F6, 0.1); }

    .search-box__input { border-color: #334155; color: #E2E8F0; &::placeholder { color: #475569; } &:focus { border-color: #3B82F6; } }

    .table-head th { color: #94A3B8; border-color: #334155; }
    .table-row td { border-color: #334155; }
    .table-row:hover td { background: rgba(255,255,255,0.03); }

    .cell-sku { color: #E2E8F0; }
    .cell-mono { color: #E2E8F0; }
    .cell-img { border-color: #334155; }
    .cell-img-empty { border-color: #334155; color: #475569; }
    .action-btn { color: #94A3B8; }

    .btn-ean--ghost { border-color: #475569; color: #94A3B8; &:hover { background: #334155; color: #E2E8F0; } }
    .btn-mini { border-color: #475569; color: #94A3B8; &:hover { border-color: #3B82F6; color: #3B82F6; } }
  }
}
</style>
